import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Alert, ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';

import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

interface Props {
  groupCompletion?: GetAchievementGroupCompletionsRes['groupCompletions'][number];
  updateGroups: () => Promise<void>;
}

const AchievementGroupOfUsersRemove = ({
  groupCompletion,
  updateGroups
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    serverConfig: { requireReasonWhenDeletingData }
  } = useReduxState();

  const remove = async (values: { deletionReason?: string }) => {
    const params = new URLSearchParams({
      ...(values.deletionReason && { deletionReason: values.deletionReason })
    });

    await api.delete(
      `/admin/achievements/${achievement.id}/group-completions` +
        (groupCompletion ? `/${groupCompletion.achievementData.id}` : '') +
        (values.deletionReason ? `?${params.toString()}` : '')
    );

    await updateGroups();
    dispatch.addNotice({ text: labels.success, type: 'success' });
  };

  const labels = groupCompletion
    ? {
        buttonText: messages.achievementGroupConversationDelete,
        confirmText: messages.achievementGroupConversationDeleteConfirm,
        success: messages.achievementGroupConversationDeleteSuccess,
        warning: messages.commonActionIsPermanent
      }
    : {
        buttonText: messages.achievementGroupConversationDeleteAll,
        confirmText: messages.achievementGroupConversationDeleteAllConfirm,
        success: messages.achievementGroupConversationDeleteAllSuccess,
        warning: messages.commonActionIsPermanent
      };

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonIcon="trash-alt"
      buttonSize="header"
      buttonIsDropdown={Boolean(groupCompletion)}
      buttonText={labels.buttonText}
      modalActionCategory="danger"
      modalActionText={messages.commonRemove}
      modalTitle={labels.buttonText}
      onSubmit={remove}
    >
      {() => (
        <>
          {labels.warning ? (
            <Alert
              style={{ marginBottom: 15 }}
              text={labels.warning}
              type="warning-light"
            />
          ) : null}

          {labels.confirmText}

          {requireReasonWhenDeletingData ? (
            <FormGroup
              htmlFor={`deletionReason-achievement-${achievement?.id || 0}-${
                groupCompletion?.achievementData.id || 0
              }`}
              label={messages.commonDeletionReason}
              style={{ marginTop: 15 }}
            >
              <Text
                field="deletionReason"
                id={`deletionReason-achievement-${achievement?.id || 0}-${
                  groupCompletion?.achievementData.id || 0
                }`}
                maxLength={255}
                autocomplete="off"
                placeholder={messages.commonInputDeletionReason}
                required={true}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementGroupOfUsersRemove;
